import { FC, ReactNode, useContext, useEffect, useMemo } from 'react';
import { useSetAtom, useAtomValue } from 'jotai';
import { useStore } from '@tanstack/react-store';

import { useWizardStep } from 'components/full-page-wizard';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import { EntitySelectionTable } from 'components/entity-selection-table';
import { Link } from 'components/link';
import { Date } from 'components/date';
import { isTiffFileName } from 'components/allocation-manager/allocation-tracts-table/utils';
import { EMPTY_CELL } from 'utils/empty-cell';
import { formatNumber } from 'utils/number';

import { AfeWizardCtx } from '../context';
import {
  saveAfeAtom,
  afeEnumsLoadableAtom,
  statesLoadableAtom,
  countiesLoadableAtom,
  wizardNewUploadsAtom,
  wizardPendingDeletesAtom,
  wizardVisibleFilesAtom,
} from '../atoms';
import { AfeWellMap } from '../components/afe-well-map';
import { buildAfePayload } from '../utils';
import type { CostItem, WizardFileRow } from '../types';
import styles from './summary-step.module.scss';

/** Minimal DataTable config with all interactive features disabled */
const READONLY_TABLE_CONFIG = {
  sorting: false,
  columnResizing: false,
  columnReordering: false,
  columnPinning: false,
  columnVisibility: false,
  globalSearch: false,
  columnFilters: false,
  fitContent: true,
  hideFooter: true,
} as const;

// ─────────────────────────────────────
// Helper sub-components
// ─────────────────────────────────────

/**
 * Renders a labeled section with a heading and separator.
 * @param {object} props - Title and children
 * @return {JSX.Element} Section with heading
 */
const SummarySection: FC<{ title: string; children: ReactNode }> = ({
  title,
  children,
}) => (
  <div className={styles.section}>
    <h3 className={styles.sectionTitle}>{title}</h3>
    {children}
  </div>
);

/**
 * Renders a label: value pair for the top info row.
 * @param {object} props - Label text and value
 * @return {JSX.Element} Field row with label and value
 */
const SummaryField: FC<{ label: string; value: string | number | null }> = ({
  label,
  value,
}) => (
  <div className={styles.fieldRow}>
    <span className={styles.fieldLabel}>{label}: </span>
    <span className={styles.fieldValue}>{value ?? EMPTY_CELL}</span>
  </div>
);

// ─────────────────────────────────────
// DataTable column definitions
// ─────────────────────────────────────

type CostRow = Record<string, unknown> & {
  id: string;
  name: string;
  category: string;
  subCategory: string;
  cost: string;
};

const costCell = (info: {
  row: { original: CostRow };
  getValue: () => unknown;
}) => {
  return info.row.original.id === 'total' ? (
    <span className={styles.costTotalCell}>{String(info.getValue())}</span>
  ) : (
    info.getValue()
  );
};

const costColumns: DataTableColumnDef<CostRow>[] = [
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Name of Cost',
    size: 150,
    cell: costCell,
  },
  { id: 'category', accessorKey: 'category', header: 'Category', size: 120 },
  {
    id: 'subCategory',
    accessorKey: 'subCategory',
    header: 'Sub Category',
    size: 120,
  },
  {
    id: 'cost',
    accessorKey: 'cost',
    header: 'Cost',
    size: 120,
    cell: costCell,
  },
];

// `DataTable<T>` constrains `T extends Record<string, unknown>`. Intersect
// with the index signature; TS still narrows the discriminated union by
// `source` inside cells. Same pattern as the wizard's Supporting
// Information step.
type WizardFileTableRow = WizardFileRow & Record<string, unknown>;

const fileColumns: DataTableColumnDef<WizardFileTableRow>[] = [
  {
    id: 'fileName',
    accessorKey: 'name',
    header: 'File Name',
    size: 360,
    meta: { type: 'text' },
    cell: ({ row }) => {
      const r = row.original;
      if (!r.href) return r.name;
      const href = r.href;
      return (
        <Link
          truncate
          to={href}
          target="_blank"
          rel="noreferrer"
          onClick={(e: React.MouseEvent) => {
            // Mirrors the TIFF workaround in supporting-information-step
            // and the detail panel: some browsers can't render TIFF inline,
            // so route the click through window.open.
            if (isTiffFileName(r.name)) {
              e.preventDefault();
              window.open(href, '_blank');
            }
          }}
        >
          {r.name}
        </Link>
      );
    },
  },
  {
    id: 'size',
    header: 'Size',
    size: 100,
    meta: { type: 'text' },
    cell: ({ row }) => row.original.size ?? EMPTY_CELL,
  },
  {
    id: 'uploaded',
    accessorKey: 'date',
    header: 'Uploaded',
    size: 130,
    meta: { type: 'text' },
    cell: ({ row }) => {
      const date = row.original.date;
      return date ? <Date date={date} format="M/DD/YYYY" /> : EMPTY_CELL;
    },
  },
];

// ─────────────────────────────────────
// SummaryStep
// ─────────────────────────────────────

/**
 * Step 6 — Summary. Read-only review of all form data from previous steps.
 * Registers a save handler via setOnNext that builds the API payload and
 * triggers the save mutation. On success, navigates back to AFE catalog.
 * @return {JSX.Element} The summary step content
 */
const SummaryStep: FC = () => {
  const { form, mode, afeId } = useContext(AfeWizardCtx)!;
  const { setOnNext, finishWizard } = useWizardStep();

  const triggerSave = useSetAtom(saveAfeAtom);
  const visibleFiles = useAtomValue(wizardVisibleFilesAtom);
  const newUploads = useAtomValue(wizardNewUploadsAtom);
  const pendingDeletes = useAtomValue(wizardPendingDeletesAtom);
  const enumsLoadable = useAtomValue(afeEnumsLoadableAtom);

  const enums = enumsLoadable.state === 'hasData' ? enumsLoadable.data : null;

  // Read form values via individual selectors
  const afeType = useStore(form.store, (s) => s.values.afeType);
  const wellType = useStore(form.store, (s) => s.values.wellType);
  const uwi = useStore(form.store, (s) => s.values.uwi);
  const importedWellName = useStore(
    form.store,
    (s) => s.values.importedWellName
  );
  const basinName = useStore(form.store, (s) => s.values.basinName);
  const reportedFormation = useStore(
    form.store,
    (s) => s.values.reportedFormation
  );
  const stateId = useStore(form.store, (s) => s.values.stateId);
  const countyId = useStore(form.store, (s) => s.values.countyId);

  // Resolve state/county names from loaded atom data
  const statesLoadable = useAtomValue(statesLoadableAtom);
  const countiesLoadable = useAtomValue(countiesLoadableAtom);

  const stateName = useMemo(() => {
    if (!stateId || statesLoadable.state !== 'hasData') return null;
    return statesLoadable.data?.find((s) => s.id === stateId)?.name ?? null;
  }, [stateId, statesLoadable]);

  const countyName = useMemo(() => {
    if (!countyId || countiesLoadable.state !== 'hasData') return null;
    return (
      countiesLoadable.data?.find((c) => c.id === countyId)?.shortName ?? null
    );
  }, [countyId, countiesLoadable]);
  const operator = useStore(form.store, (s) => s.values.operator);
  const plannedLateralLength = useStore(
    form.store,
    (s) => s.values.plannedLateralLength
  );
  const plannedTvd = useStore(form.store, (s) => s.values.plannedTvd);

  const wellName = useStore(form.store, (s) => s.values.wellName);
  const wellOperator = useStore(form.store, (s) => s.values.wellOperator);
  const wellUnit = useStore(form.store, (s) => s.values.wellUnit);
  const drilledLateralLength = useStore(
    form.store,
    (s) => s.values.drilledLateralLength
  );
  const trueVerticalDepth = useStore(
    form.store,
    (s) => s.values.trueVerticalDepth
  );

  const entities = useStore(form.store, (s) => s.values.entities);
  const costItems = useStore(form.store, (s) => s.values.costItems);
  const notes = useStore(form.store, (s) => s.values.notes);

  const showDcAndE = afeType === 'dc_and_e';
  const isExistingWell = wellType === 'existing_well';

  // Resolve enum labels
  const afeTypeLabel = useMemo(
    () => enums?.afeType.find((o) => o.value === afeType)?.label ?? afeType,
    [enums, afeType]
  );
  const wellTypeLabel = useMemo(
    () => enums?.wellType.find((o) => o.value === wellType)?.label ?? wellType,
    [enums, wellType]
  );

  // Total cost
  const total = useMemo(
    () => costItems.reduce((sum, item) => sum + (item.cost ?? 0), 0),
    [costItems]
  );

  // Transform cost items for DataTable, with a total row appended
  const costRows: CostRow[] = useMemo(
    () => [
      ...costItems.map((item: CostItem, index: number) => ({
        id: String(item.id ?? index),
        name: item.name,
        category: item.category,
        subCategory: item.subCategory,
        cost:
          item.cost !== null
            ? formatNumber(item.cost, 0, 0, 'currency')
            : EMPTY_CELL,
      })),
      {
        id: 'total',
        name: 'Total',
        category: '',
        subCategory: '',
        cost: formatNumber(total, 0, 0, 'currency'),
      },
    ],
    [costItems, total]
  );

  // Register save handler. Single transactional call — new uploads and
  // pending deletes ride inline on the save payload via
  // `supporting_files_attributes`. On failure, `triggerSave` throws and the
  // navigation lines below don't run, leaving the user on the Summary step
  // with `wizardNewUploadsAtom` + `wizardPendingDeletesAtom` state intact
  // so a retry doesn't need to re-upload or re-mark deletions.
  useEffect(() => {
    setOnNext(async () => {
      const payload = buildAfePayload(
        form.state.values,
        newUploads,
        pendingDeletes
      );
      await triggerSave({ payload, mode, afeId });
      finishWizard('/afe_catalog');
    });
  }, [
    form,
    triggerSave,
    newUploads,
    pendingDeletes,
    mode,
    afeId,
    finishWizard,
    setOnNext,
  ]);

  return (
    <div className={styles.splitLayout}>
      <div className={styles.contentPanel}>
        <h2 className={styles.heading}>Summary</h2>
        <p className={styles.subtitle}>
          Confirm information entered to create AFE.
        </p>

        {/* Top info row */}
        <div className={styles.topRow}>
          {/* AFE Type */}
          <div className={styles.topColumn}>
            <div className={styles.columnHeading}>AFE Type</div>
            <div className={styles.fieldValue}>{afeTypeLabel}</div>
          </div>

          {/* Well Information */}
          <div className={styles.topColumn}>
            <div className={styles.columnHeading}>Well Information</div>
            <SummaryField label="Well Type" value={wellTypeLabel} />
            {isExistingWell ? (
              <>
                <SummaryField label="UWI" value={uwi} />
                <SummaryField label="Basin" value={basinName} />
                <SummaryField
                  label="Reported Formation"
                  value={reportedFormation}
                />
                {showDcAndE && (
                  <>
                    <SummaryField
                      label="Planned Lateral Length"
                      value={
                        plannedLateralLength !== null
                          ? `${formatNumber(plannedLateralLength, 0)} ft`
                          : null
                      }
                    />
                    <SummaryField
                      label="Planned Total Depth (TD)"
                      value={
                        plannedTvd !== null
                          ? `${formatNumber(plannedTvd, 0)} ft`
                          : null
                      }
                    />
                  </>
                )}
              </>
            ) : (
              <>
                <SummaryField
                  label="Imported Well Name"
                  value={importedWellName}
                />
                <SummaryField label="Basin" value={basinName} />
                <SummaryField label="State" value={stateName} />
                <SummaryField label="County" value={countyName} />
                <SummaryField label="Operator" value={operator} />
                <SummaryField
                  label="Reported Formation"
                  value={reportedFormation}
                />
              </>
            )}
          </div>

          {/* Well Lookup (existing well) or D&C fields (imported well) */}
          {isExistingWell ? (
            <div className={styles.topColumn}>
              <div className={styles.columnHeading}>Well Lookup</div>
              <SummaryField label="Well Name" value={wellName} />
              <SummaryField label="Operator" value={wellOperator} />
              <div className={styles.fieldRow}>
                <span className={styles.fieldLabel}>Unit: </span>
                <span className={styles.fieldValue}>
                  {wellUnit ? (
                    <a href={`/development_areas/${wellUnit}`}>{wellUnit}</a>
                  ) : (
                    EMPTY_CELL
                  )}
                </span>
              </div>
              <SummaryField
                label="Drilled Lateral Length"
                value={
                  drilledLateralLength !== null
                    ? `${formatNumber(drilledLateralLength, 0)} ft`
                    : null
                }
              />
              <SummaryField
                label="True Vertical Depth (TVD)"
                value={
                  trueVerticalDepth !== null
                    ? `${formatNumber(trueVerticalDepth, 0)} ft`
                    : null
                }
              />
            </div>
          ) : (
            showDcAndE && (
              <div className={styles.topColumn}>
                <SummaryField
                  label="Planned Lateral Length"
                  value={
                    plannedLateralLength !== null
                      ? `${formatNumber(plannedLateralLength, 0)} ft`
                      : null
                  }
                />
                <SummaryField
                  label="Planned Total Depth"
                  value={
                    plannedTvd !== null
                      ? `${formatNumber(plannedTvd, 0)} ft`
                      : null
                  }
                />
              </div>
            )
          )}

          {/* Total AFE Cost */}
          <div className={styles.totalColumn}>
            <div className={styles.totalLabel}>Total AFE Cost</div>
            <div className={styles.totalValue}>
              {formatNumber(total, 0, 0, 'currency')}
            </div>
          </div>
        </div>

        {/* Entities */}
        <SummarySection title="Entities:">
          <EntitySelectionTable entities={entities} />
        </SummarySection>

        {/* Cost Summary */}
        <SummarySection title="Cost Summary">
          <DataTable
            data={costRows}
            columns={costColumns}
            getRowId={(row) => row.id}
            config={READONLY_TABLE_CONFIG}
          />
        </SummarySection>

        {/* Supporting Information */}
        <SummarySection title="Supporting Information">
          {visibleFiles.length > 0 && (
            <div className={styles.filesTable}>
              <DataTable<WizardFileTableRow>
                data={visibleFiles as WizardFileTableRow[]}
                columns={fileColumns}
                getRowId={(row) => row.key}
                config={READONLY_TABLE_CONFIG}
              />
            </div>
          )}
          <div>
            <div className={styles.supportingLabel}>Notes</div>
            <div className={styles.supportingValue}>{notes || EMPTY_CELL}</div>
          </div>
        </SummarySection>
      </div>
      <div className={styles.mapPanel}>
        <AfeWellMap uwi={uwi} />
      </div>
    </div>
  );
};

export { SummaryStep };
